import React, {useEffect, useMemo} from 'react';
import {Material} from '@wonderlandengine/api';
import {SpawnPieceButton} from '../../components/spawn-piece-button.js';
import {Color} from '../utils/utils.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {FlatWith2ColorsOpaqueTextured} from '../../types/materials.js';
import {BombButtonTutorial} from '../../components/bomb-button-tutorial.js';
import {FruitSmasherButton} from '../../components/fruit-smasher-button.js';
import {AdManager, AdRewardTypes} from '../../components/ad-manager.js';
import {PauseUnpause, PauseUnpauseAction} from '../../components/pause-unpause.js';

/**
 * React Hook for use with the Coconut Bomb(=SpawnPieceButton) component
 * @returns
 */
export const useCoconutBomb = (): [
    number,
    string,
    Color,
    () => void,
    Material,
    (e) => void,
] => {
    //cooldown is a value between 0 and 1
    const [cooldown, setCooldown] = React.useState(SpawnPieceButton.instance.sliderValue);
    const [inventory, setInventory] = React.useState(
        SpawnPieceButton.instance.myInventoryDisplayText
    );
    const [inventoryColor, setInventoryColor] = React.useState<Color>(
        Float32Array.from(SpawnPieceButton.instance.myInventoryDisplayColor)
    );

    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;

    const cooldownMat = useMemo(() => {
        return rotbMats.cooldownMaterial.clone() as FlatWith2ColorsOpaqueTextured;
    }, []);

    const onClick = () => {
        SpawnPieceButton.instance.onClick();
    };

    const watchAd = (eventData) => {
        PauseUnpause.instance.onClick(PauseUnpauseAction.Pause);
        try {
            AdManager.instance
                .getAdReward(AdRewardTypes.RemoveCooldown, eventData.e)
                .then(({type, coins}) => {
                    SpawnPieceButton.instance.clearCooldown();
                    PauseUnpause.instance.onClick(PauseUnpauseAction.Unpause);
                })
                .finally(() => {});
        } catch (e) {
            console.log(`Error during ad: ${e} `);
            PauseUnpause.instance.onClick(PauseUnpauseAction.Unpause);
        }
    };

    useEffect(() => {
        const buttonComponentRef = SpawnPieceButton.instance;
        const handlePropChange = () => {
            setCooldown(buttonComponentRef.sliderValue);
            setInventory(buttonComponentRef.myInventoryDisplayText);
            setInventoryColor(
                Float32Array.from(buttonComponentRef.myInventoryDisplayColor)
            );
        };

        buttonComponentRef.cooldownMaterial = cooldownMat;
        buttonComponentRef.updateMaterial();
        buttonComponentRef.propertyChanged.add(handlePropChange);
        handlePropChange();
        return () => {
            buttonComponentRef.propertyChanged.remove(handlePropChange);
        };
    }, []);

    return [cooldown, inventory, inventoryColor, onClick, cooldownMat, watchAd];
};

export const useBombTutorial = (): [number, string, Color, () => void, Material] => {
    const [cooldown, setCooldown] = React.useState(BombButtonTutorial.instance.sliderValue);
    const [inventory, setInventory] = React.useState(
        BombButtonTutorial.instance.myInventoryDisplayText
    );
    const [inventoryColor, setInventoryColor] = React.useState<Color>(
        Float32Array.from(BombButtonTutorial.instance.myInventoryDisplayColor)
    );

    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;

    const cooldownMat = useMemo(() => {
        return rotbMats.cooldownMaterial.clone() as FlatWith2ColorsOpaqueTextured;
    }, []);

    const onClick = () => {
        BombButtonTutorial.instance.onClick();
    };

    useEffect(() => {
        const buttonComponentRef = BombButtonTutorial.instance;
        const handlePropChange = () => {
            setCooldown(buttonComponentRef.sliderValue);
            setInventory(buttonComponentRef.myInventoryDisplayText);
            setInventoryColor(
                Float32Array.from(buttonComponentRef.myInventoryDisplayColor)
            );
        };

        buttonComponentRef.cooldownMaterial = cooldownMat;
        buttonComponentRef.updateMaterial();
        buttonComponentRef.propertyChanged.add(handlePropChange);
        handlePropChange();
        return () => {
            buttonComponentRef.propertyChanged.remove(handlePropChange);
        };
    }, []);

    return [cooldown, inventory, inventoryColor, onClick, cooldownMat];
};

/**
 * React Hook for use with the Fruit Hammer component
 * @returns
 */
export const useFruitHammer = (): [
    number,
    string,
    Color,
    () => void,
    Material,
    (e) => void,
] => {
    //cooldown is a value between 0 and 1
    const [cooldown, setCooldown] = React.useState(FruitSmasherButton.instance.sliderValue);
    const [inventory, setInventory] = React.useState(
        FruitSmasherButton.instance.myInventoryDisplayText
    );
    const [inventoryColor, setInventoryColor] = React.useState<Color>(
        Float32Array.from(FruitSmasherButton.instance.myInventoryDisplayColor)
    );

    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;

    const cooldownMat = useMemo(() => {
        return rotbMats.cooldownMaterial.clone() as FlatWith2ColorsOpaqueTextured;
    }, []);

    const onClick = () => {
        FruitSmasherButton.instance.onClick();
    };

    useEffect(() => {
        const buttonComponentRef = FruitSmasherButton.instance;
        const handlePropChange = () => {
            setCooldown(buttonComponentRef.sliderValue);
            setInventory(buttonComponentRef.myInventoryDisplayText);
            setInventoryColor(
                Float32Array.from(buttonComponentRef.myInventoryDisplayColor)
            );
        };
        buttonComponentRef.cooldownMaterial = cooldownMat;
        buttonComponentRef.updateMaterial();
        buttonComponentRef.propertyChanged.add(handlePropChange);
        handlePropChange();
        return () => {
            buttonComponentRef.propertyChanged.remove(handlePropChange);
        };
    }, []);

    const watchAd = (eventData) => {
        PauseUnpause.instance.onClick(PauseUnpauseAction.Pause);
        try {
            AdManager.instance
                .getAdReward(AdRewardTypes.RemoveCooldown, eventData.e)
                .then(({type, coins}) => {
                    FruitSmasherButton.instance.clearCooldown();
                    PauseUnpause.instance.onClick(PauseUnpauseAction.Unpause);
                })
                .finally(() => {});
        } catch (e) {
            console.log(`Error during ad: ${e} `);
            PauseUnpause.instance.onClick(PauseUnpauseAction.Unpause);
        }
    };

    return [cooldown, inventory, inventoryColor, onClick, cooldownMat, watchAd];
};
